import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { BehaviorSubject, Subscription } from 'rxjs';
import firebase from 'firebase/app';
export type CallStatus = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
@Injectable({ providedIn: 'root' })
export class AudioCallService {
public callStatus$ = new BehaviorSubject<CallStatus>('idle');
  private localStreamBs = new BehaviorSubject<MediaStream>(null);
  public localStream$ = this.localStreamBs.asObservable();

  private remoteStreamBs = new BehaviorSubject<MediaStream>(null);
  public remoteStream$ = this.remoteStreamBs.asObservable();

  public isCallStarted$ = new BehaviorSubject<boolean>(false);

  private pc: RTCPeerConnection;
  private roomId: string;
  private selfId: string = `audio-${Date.now()}`;

  private signalsSub: Subscription;
  private candidatesSub: Subscription;
  private participantsSub: Subscription;

  private signalingLocked = false;
  private roomJoined = false;
  private pendingIce: RTCIceCandidateInit[] = [];

  constructor(private afs: AngularFirestore) {}

  async initLocalStream() {
    if (!this.localStreamBs.value) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false
      });
      this.localStreamBs.next(stream);
    }
    return this.localStreamBs.value;
  }

  async createOrJoinRoom(roomId: string) {
    if (this.roomJoined) return;
    this.callStatus$.next('calling');


    this.roomJoined = true;
    this.roomId = roomId;

    await this.initLocalStream();

    const roomRef = this.afs.collection('rooms').doc(roomId);

    // clear old signals
    const sig = await roomRef.collection('signals').ref.get();
    sig.forEach(d => d.ref.delete());

    const cand = await roomRef.collection('candidates').ref.get();
    cand.forEach(d => d.ref.delete());

    this.startSignalingListeners(roomId);

    const snap = await roomRef.ref.get();
    if (!snap.exists) {
      await roomRef.set({
        createdBy: this.selfId,
        participants: { [this.selfId]: true }
      });
    } else {
      await roomRef.update({
        [`participants.${this.selfId}`]: true
      });
    }

    this.participantsSub = roomRef.valueChanges().subscribe((data: any) => {
      if (!data?.participants) return;

      const ids = Object.keys(data.participants);

      if (ids.length < 2) {
        this.forceRemoteHangup();
        return;
      }

      this.decideSignalingRole(data);
    });
  }

  private forceRemoteHangup() {
     this.callStatus$.next('ended');
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    this.pendingIce = [];
    this.signalingLocked = false;
    this.remoteStreamBs.next(null);
    this.isCallStarted$.next(false);
  }

  private decideSignalingRole(data: any) {
    if (this.signalingLocked) return;
    const ids = Object.keys(data.participants);
    if (ids.length < 2) return;

    const sorted = [...ids].sort();
    const otherId = sorted.find(x => x !== this.selfId);
    const iAmCaller = this.selfId.localeCompare(otherId) < 0;

    if (iAmCaller && !this.pc) {
      this.signalingLocked = true;
      setTimeout(() => this.initiateOffer(otherId), 100);
    }
  }

  private async initiateOffer(remoteId: string) {
    this.pc = this.createPeerConnection(remoteId);

    const stream = this.localStreamBs.value;
    stream.getTracks().forEach(t => this.pc.addTrack(t, stream));

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    await this.afs.collection('rooms').doc(this.roomId)
      .collection('signals')
      .add({
        from: this.selfId,
        to: remoteId,
        type: 'offer',
        sdp: offer.sdp,
        timestamp: Date.now()
      });
  }

  private startSignalingListeners(roomId: string) {
    const roomRef = this.afs.collection('rooms').doc(roomId);

    this.signalsSub = roomRef.collection('signals')
      .stateChanges(['added'])
      .subscribe(async changes => {
        for (const ch of changes) {
          const s = ch.payload.doc.data() as any;
          if (s.to !== this.selfId) continue;

          if (s.type === 'offer') {this.callStatus$.next('ringing');await this.handleIncomingOffer(s);}
          if (s.type === 'answer') {
            await this.pc.setRemoteDescription(new RTCSessionDescription(s));
            this.flushIce();
            this.isCallStarted$.next(true);
            this.callStatus$.next('connected');
          }
        }
      });

    this.candidatesSub = roomRef.collection('candidates')
      .stateChanges(['added'])
      .subscribe(changes => {
        changes.forEach(c => {
          const d = c.payload.doc.data() as any;
          if (d.to === this.selfId) {
            if (this.pc?.remoteDescription)
              this.pc.addIceCandidate(new RTCIceCandidate(d));
            else
              this.pendingIce.push(d);
          }
        });
      });
  }

  private async handleIncomingOffer(offer: any) {
    this.pc = this.createPeerConnection(offer.from);

    const stream = this.localStreamBs.value;
    stream.getTracks().forEach(t => this.pc.addTrack(t, stream));

    await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
    this.flushIce();

    const ans = await this.pc.createAnswer();
    await this.pc.setLocalDescription(ans);

    await this.afs.collection('rooms').doc(this.roomId)
      .collection('signals')
      .add({
        from: this.selfId,
        to: offer.from,
        type: 'answer',
        sdp: ans.sdp,
        timestamp: Date.now()
      });

    this.isCallStarted$.next(true);
  }

  private flushIce() {
    this.pendingIce.forEach(c => this.pc.addIceCandidate(new RTCIceCandidate(c)));
    this.pendingIce = [];
  }

  private createPeerConnection(remoteId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        {
          urls: 'turn:57.159.29.101:3478?transport=udp',
          username: 'ezoturnvm1',
          credential: 'SBY31a54CCEMtzAMH0Ptg5v86'
        },
        {
          urls: 'turn:57.159.29.101:3478?transport=tcp',
          username: 'ezoturnvm1',
          credential: 'SBY31a54CCEMtzAMH0Ptg5v86'
        },
        {
          urls: 'turns:57.159.29.101:5349',
          username: 'ezoturnvm1',
          credential: 'SBY31a54CCEMtzAMH0Ptg5v86'
        }
      ]
    });

    pc.onicecandidate = e => {
      if (e.candidate) {
        this.afs.collection('rooms').doc(this.roomId)
          .collection('candidates').add({
            from: this.selfId,
            to: remoteId,
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex
          });
      }
    };

    pc.ontrack = e => {
      this.remoteStreamBs.next(e.streams[0]);
      this.callStatus$.next('connected');
    };

    return pc;
  }

  async endCall() {
    this.signalsSub?.unsubscribe();
    this.candidatesSub?.unsubscribe();
    this.participantsSub?.unsubscribe();
    this.callStatus$.next('ended');

    this.localStreamBs.value?.getTracks().forEach(t => t.stop());
    this.localStreamBs.next(null);

    this.pc?.close();
    this.pc = null;

    await this.afs.collection('rooms').doc(this.roomId).update({
      [`participants.${this.selfId}`]: firebase.firestore.FieldValue.delete()
    });

    this.remoteStreamBs.next(null);
    this.isCallStarted$.next(false);
    this.signalingLocked = false;
    this.roomJoined = false;

  }

  toggleMic() {
    const t = this.localStreamBs.value?.getAudioTracks()[0];
    if (t) t.enabled = !t.enabled;
  }
}

